// frontend/src/components/LoginForm.js
import React, { useState, useContext } from 'react';
import { AuthContext } from './AuthContext';

const profileConfig = {
  general: {
    title: 'Iniciar sesion',
    subtitle: 'Accede con tu cuenta y luego el sistema te llevara al panel que corresponde.',
    submitLabel: 'Ingresar',
    registerView: 'registerStudent',
    registerLabel: 'Registrate aqui'
  },
  student: {
    title: 'Acceso estudiante',
    subtitle: 'Ingresa para revisar tu aprendizaje, cursos comprados y avances.',
    submitLabel: 'Ingresar como estudiante',
    registerView: 'registerStudent',
    registerLabel: 'Crear cuenta de estudiante'
  },
  instructor: {
    title: 'Acceso instructor',
    subtitle: 'Ingresa para administrar tus cursos, contenido y estadisticas.',
    submitLabel: 'Ingresar como instructor',
    registerView: 'registerInstructor',
    registerLabel: 'Crear cuenta de instructor'
  },
  admin: {
    title: 'Acceso administrador',
    subtitle: 'Ingresa para revisar usuarios, cursos y configuracion general.',
    submitLabel: 'Ingresar como administrador',
    registerView: null,
    registerLabel: null
  }
};

const directoryProfiles = [
  {
    key: 'student',
    title: 'Estudiante',
    description: 'Cursos inscritos, progreso y aprendizaje.'
  },
  {
    key: 'instructor',
    title: 'Instructor',
    description: 'Publicacion, gestion de cursos y panel docente.'
  }
];

const LoginForm = ({ onLoginSuccess, onNavigate, profile = 'general' }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useContext(AuthContext);
  const activeProfile = profileConfig[profile] || profileConfig.general;
  const quickLinks = [
    {
      label: 'Acceso estudiante',
      href: '/?access=student'
    },
    {
      label: 'Portal instructor',
      action: () => onNavigate?.('loginInstructor')
    }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await login(email, password);

      if (result?.success && result?.user) {
        if (onLoginSuccess) {
          onLoginSuccess(result.user);
        } else if (onNavigate) {
          onNavigate(
            result.user.role === 'admin'
              ? 'adminDashboard'
              : result.user.role === 'instructor'
                ? 'instructorDashboard'
                : 'studentDashboard'
          );
        }
      } else {
        setError(result?.message || 'Error en la autenticacion');
      }
    } catch (err) {
      setError(err.message || 'Error al iniciar sesion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto bg-white p-8 rounded-lg shadow-md mt-10">
      <h2 className="text-2xl font-bold mb-2 text-center">{activeProfile.title}</h2>
      <p className="text-sm text-gray-600 text-center mb-6">{activeProfile.subtitle}</p>
      {error && <p className="text-red-500 mb-4 text-center">{error}</p>}
      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label className="block text-gray-700 mb-2">Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-purple-600"
            required
          />
        </div>
        <div className="mb-6">
          <label className="block text-gray-700 mb-2">Contrasena</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-purple-600"
            required
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          className={`w-full ${loading ? 'bg-purple-400' : 'bg-purple-600'} text-white py-2 rounded hover:bg-purple-700 transition-colors mb-4`}
        >
          {loading ? 'Cargando...' : activeProfile.submitLabel}
        </button>

        {activeProfile.registerView && (
          <div className="text-center text-sm text-gray-600">
            No tienes cuenta?{' '}
            <button
              type="button"
              onClick={() => onNavigate?.(activeProfile.registerView)}
              className="text-purple-600 hover:text-purple-800 font-medium"
            >
              {activeProfile.registerLabel}
            </button>
          </div>
        )}

        <div className="mt-6 border-t pt-4">
          <p className="text-sm text-gray-600 text-center mb-3">Enlaces de acceso rapido</p>
          <div className="flex flex-col gap-2">
            {quickLinks.map((link) => (
              link.href ? (
                <a
                  key={link.label}
                  href={link.href}
                  className="block w-full text-center border border-purple-200 text-purple-700 py-2 rounded hover:bg-purple-50 transition-colors"
                >
                  {link.label}
                </a>
              ) : (
                <button
                  key={link.label}
                  type="button"
                  onClick={link.action}
                  className="block w-full text-center border border-purple-200 text-purple-700 py-2 rounded hover:bg-purple-50 transition-colors"
                >
                  {link.label}
                </button>
              )
            ))}
          </div>
        </div>

        <div className="mt-6 border-t pt-4">
          <p className="text-sm text-gray-600 text-center mb-3">Directorio por perfil</p>
          <div className="space-y-2">
            {directoryProfiles.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => onNavigate?.(
                  item.key === 'student'
                    ? 'loginStudent'
                    : item.key === 'instructor'
                      ? 'loginInstructor'
                      : 'loginAdmin'
                )}
                className={`w-full text-left border rounded-lg px-4 py-3 transition-colors ${
                  profile === item.key
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
                }`}
              >
                <div className="font-semibold text-gray-900">{item.title}</div>
                <div className="text-sm text-gray-600">{item.description}</div>
              </button>
            ))}
          </div>
        </div>
      </form>
    </div>
  );
};

export default LoginForm;
